import { StorageImage } from "@/components/StorageImage";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEntryCollections, usePlotData, usePlots } from "@/hooks/useFirestore";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import type { Experiment } from "@/types/experiment";
import type { Trial } from "@/types/trial";
import { PlotData } from "@/hooks/usePlotData.types";
import { getTraitColor, getTraitIcon, sortTraitsByTypeAndName } from "@/lib/traitIcons";
import { cn } from "@/lib/utils";
import {
  generateTaskZip,
  generateTaskZipViaCloudFunction,
  countMediaFiles,
  ExportProgress,
  MEDIA_FILE_THRESHOLD,
  MediaDownloadOptions,
  ALL_MEDIA_OPTIONS,
  NO_MEDIA_OPTIONS,
  hasAnyMediaOption,
  hasAllMediaOptions,
} from "@/services/csvExportService";
import { downloadBlob } from "@/services/annotatedImageExportService";
import { resolveCocoExportSourceFromSnapshots } from "@/services/cocoExportService";
import {
  buildOptimisticDeferredAnalysisJobSnapshot,
  getDeferredAnalysisJobProgressText,
  getDeferredAnalysisJobId,
  isDeferredAnalysisJobSnapshotActive,
  isDeferredAnalysisUiEnabled,
  startDeferredAnalysisJob,
  subscribeToDeferredAnalysisJob,
  type DeferredAnalysisJobSnapshot,
} from "@/services/deferredAnalysisService";
import {
  buildOptimisticExportJobSnapshot,
  getExportDownloadUrl,
  getExportJobId,
  getExportJobProgressText,
  isExportJobExpired,
  isExportJobLikelyStuck,
  isExportJobSnapshotActive,
  subscribeToExportJob,
  type ExportJobSnapshot,
} from "@/services/exportJobService";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfigUnion } from "@/types/entryConfig";
import type { CaptureMetadata } from "@shared/types/annotations";
import { Plot } from "@/types/plot";
import { Task } from "@/types/task";
import { getDeferredAnalysisPlotStatus, summarizeDeferredAnalysisForTask } from "@/utils/deferredAnalysisUtils";
import { extractImagesFromEntryCollection, formatTimerDuration, getAnnotationMetadata } from "@/utils/entryUtils";
import {
  computeGradeDistribution,
  type GradeDistribution,
} from "@/utils/gradingUtils";
import { getPlotMetadataEntries } from "@/utils/plotMetadataUtils";
import { getTraitStats, getPhotoStats } from "@/utils/traitStatsUtils";
import * as d3 from "d3";
import { Download, Info, Loader2, ImageOff, PlayCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { LazyStorageImage } from "./LazyStorageImage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getAllEntryValues,
  getCollectedEntryCount,
  getEntryDisplayValueWithStatus,
  AggregatedValueResult,
} from "../utils/entryCollectionUtils";
import {
  MeasurementMetric,
  ImageMeasurementMetric,
  StandCountMetric,
  RowPlantedStandCountMetric,
  formatMetricValue,
} from "@/types/measurementMetrics";
import { formatRoundedCountPerSquareMeterPerAcre } from "@/lib/standCountUtils";
import { MeasurementMetricTabs } from "@/components/MeasurementMetricTabs";
import { Timestamp } from "firebase/firestore";

const GRADE_METADATA_FETCH_CONCURRENCY = 5;

interface GradeMetadataTarget {
  path: string;
  updatedAt?: Timestamp;
}

async function fetchGradeMetadataByPath(targets: GradeMetadataTarget[]): Promise<Map<string, CaptureMetadata>> {
  const next = new Map<string, CaptureMetadata>();
  let currentIndex = 0;
  const workerCount = Math.min(GRADE_METADATA_FETCH_CONCURRENCY, targets.length);

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (currentIndex < targets.length) {
        const target = targets[currentIndex];
        currentIndex += 1;
        try {
          const metadata = await getAnnotationMetadata(target.path, target.updatedAt);
          if (metadata) next.set(target.path, metadata);
        } catch {
          // Missing metadata means this capture cannot contribute grade data.
        }
      }
    })
  );

  return next;
}

const getGradeMetadataTargetVersion = (updatedAt?: Timestamp): string =>
  updatedAt ? `${updatedAt.seconds}.${updatedAt.nanoseconds}` : "unversioned";

interface D3HeatmapGridProps {
  task: Task;
  trial?: Trial | null;
  trialId?: string;
  experimentId?: string;
  taskId?: string;
  /** When true (a ready AI-trait bundle exists for this task), show a "View AI Traits" entry point. */
  aiTraitsBundleReady?: boolean;
  availableEntryConfigs: EntryConfigUnion[];
  selectedEntryConfigId: string | null;
  onEntryConfigChange: (entryConfigId: string) => void;
  /** Owned by the page, not fetched here, so this grid and the plot details sheet
   *  share one experiment subscription and render off the same snapshot. */
  experiment?: Experiment | null;
  onPlotSelect?: (plot: Plot, entryCollections: EntryCollection[]) => void;
  measurementMetric?: MeasurementMetric | null;
  onMeasurementMetricChange?: (metric: MeasurementMetric) => void;
  isDownloadDialogOpen: boolean;
  onDownloadDialogOpenChange: (open: boolean) => void;
}

interface HeatmapDataPoint {
  row: number;
  col: number;
  plotId: string;
  value: number | string | null;
  rawData: PlotData;
  hasError?: boolean;
  errorReason?: "no_valid_counts" | "no_entries";
  analysisStatus?: "pending" | "processing" | "failed" | "stale";
  analysisError?: string;
  analysisUnreviewed?: boolean;
  collectedEntryCount?: number;
  isCollectedEmpty?: boolean;
  /** Populated when `measurementMetric === "grade"` for an image-measurement trait. */
  gradeDistribution?: GradeDistribution;
}

interface TooltipData {
  x: number;
  /** Top edge of the hovered cell, in viewport coords. */
  y: number;
  /** Bottom edge of the hovered cell - where the tooltip anchors when flipped below. */
  bottom: number;
  data: HeatmapDataPoint;
}

/** Above or below the hovered cell. Resolved by measuring; see `measureTooltip`. */
type TooltipPlacement = "above" | "below";

/** Gap between the hovered cell and the tooltip's nearest edge, in pixels. */
const TOOLTIP_CELL_GAP = 10;

/**
 * The plot map columns the user uploaded for this plot (variety, treatment, ...),
 * listed under the trait values in the hover tooltip. Renders nothing when the
 * plot carries no metadata, so no empty section or stray divider appears.
 */
const TooltipPlotMetadata: React.FC<{ plot: Plot; plotMap?: Experiment["plotMap"] }> = ({ plot, plotMap }) => {
  const entries = getPlotMetadataEntries(plot, plotMap);
  if (entries.length === 0) return null;

  const visible = entries.slice(0, MAX_TOOLTIP_METADATA_FIELDS);
  const hiddenCount = entries.length - visible.length;

  return (
    <div
      data-testid="heatmap-tooltip-metadata"
      className="border-t pt-2">
      <dl className="space-y-1">
        {visible.map((entry) => (
          <div
            key={entry.label}
            className="flex items-start justify-between gap-3 text-xs">
            <dt
              data-testid="heatmap-tooltip-metadata-label"
              className="flex-shrink-0 text-gray-500">
              {entry.label}
            </dt>
            <dd className="max-w-[12rem] break-words text-right font-medium">{entry.value}</dd>
          </div>
        ))}
      </dl>
      {hiddenCount > 0 && (
        <div
          data-testid="heatmap-tooltip-metadata-overflow"
          className="mt-1 text-xs text-muted-foreground">
          +{hiddenCount} more
        </div>
      )}
    </div>
  );
};

// Display mode for the heatmap: grid (semantic row/col) or list (sequential layout)
type DisplayMode = "grid" | "list";

// Maximum number of columns in list view mode
const LIST_VIEW_MAX_COLUMNS = 7;

// Side of one heatmap cell, in pixels. The row/column headers are sized from this
// too, so a frozen header always lines up with the cells it labels.
const GRID_CELL_SIZE = 96;

// How many uploaded plot map columns the hover tooltip lists before collapsing the
// rest into a "+N more" line. The tooltip flips below its cell when it will not fit
// above, but nothing bounds a wide plot map's column count, so a cap is still what
// keeps the box from outgrowing the viewport in both directions.
// The full set stays available by clicking through to the plot details sheet.
const MAX_TOOLTIP_METADATA_FIELDS = 6;

// One header cell, matching the heatmap cell it labels. Sized from GRID_CELL_SIZE
// rather than Tailwind so a frozen header can never drift from the d3 band scale.
const HEADER_CELL_STYLE: React.CSSProperties = {
  width: GRID_CELL_SIZE,
  height: GRID_CELL_SIZE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/*
 * Paint order inside the grid viewport, applied as Tailwind `z-*` at each usage:
 *
 *   z-10    scroll fades
 *   z-[15]  row headers   (pinned left)
 *   z-20    column header row (pinned top)
 *   z-[1]   corner cell, scoped to the stacking context the header row creates,
 *           so it only has to outrank its sibling column headers
 *
 * The fades sit BELOW both frozen header bands on purpose. Those bands are always
 * on screen, so letting a fade wash over them would leave the edge numbers dim -
 * the bottom fade would dim the row numbers, the right fade the column numbers.
 * Only data cells fade.
 */

const DEFERRED_STATUS_DISPLAY = {
  pending: {
    label: "Pending",
    fill: "#fef3c7",
    stroke: "#f59e0b",
    tooltipLabel: "pending",
    tooltipDescription: "This uploaded capture is waiting for cloud analysis.",
  },
  processing: {
    label: "Running",
    fill: "#dbeafe",
    stroke: "#3b82f6",
    tooltipLabel: "Running",
    tooltipDescription: "Cloud analysis is currently processing this capture.",
  },
  failed: {
    label: "Failed",
    fill: "#fecaca",
    stroke: "#ef4444",
    tooltipLabel: "failed",
    tooltipDescription: "Cloud analysis failed for this capture.",
  },
  stale: {
    label: "Stuck",
    fill: "#fed7aa",
    stroke: "#f97316",
    tooltipLabel: "Stuck",
    tooltipDescription: "Analysis appears stuck and can be retried.",
  },
} as const;

type TimestampLike = {
  seconds: number;
  nanoseconds?: number;
};

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "seconds" in value &&
    typeof (value as { seconds?: unknown }).seconds === "number"
  );
}

function getErrorName(value: unknown): string | undefined {
  return value instanceof Error ? value.name : undefined;
}

function getErrorMessage(value: unknown): string | undefined {
  return value instanceof Error ? value.message : undefined;
}

const D3HeatmapGrid: React.FC<D3HeatmapGridProps> = ({
  task,
  trial,
  trialId,
  experimentId,
  taskId,
  aiTraitsBundleReady,
  availableEntryConfigs,
  selectedEntryConfigId,
  onEntryConfigChange,
  experiment,
  onPlotSelect,
  measurementMetric,
  onMeasurementMetricChange,
  isDownloadDialogOpen,
  onDownloadDialogOpenChange: setIsDownloadDialogOpen,
}) => {
  const orgPaths = useOrgPaths();
  // "View AI Traits" entry point — rendered in every header (incl. the empty-state early returns) so a
  // task with a ready bundle never loses the link, even when the heatmap itself has nothing to show.
  const aiTraitsButton =
    aiTraitsBundleReady && taskId && trialId && experimentId ? (
      <Button
        asChild
        variant="outline"
        size="sm"
        className="border-agri-green text-agri-green hover:bg-agri-green hover:text-white">
        <Link
          to={orgPaths.taskAiTraits(trialId, experimentId, taskId)}
          data-view-ai-traits="true">
          <Sparkles className="h-4 w-4 mr-1" />
          View AI Traits
        </Link>
      </Button>
    ) : null;
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<ExportProgress | null>(null);
  const [mediaOptions, setMediaOptions] = useState<MediaDownloadOptions>(ALL_MEDIA_OPTIONS);
  const cancelDownloadRef = useRef(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipPlacement, setTooltipPlacement] = useState<TooltipPlacement>("above");
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isStartingDeferredAnalysis, setIsStartingDeferredAnalysis] = useState(false);
  const [deferredAnalysisJob, setDeferredAnalysisJob] = useState<DeferredAnalysisJobSnapshot | null>(null);
  const [isDeferredAnalysisJobLoading, setIsDeferredAnalysisJobLoading] = useState(false);
  const { selectedOrganization, userRole, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  // Check if grid can scroll and update indicator
  const updateScrollIndicator = useCallback(() => {
    if (gridContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } =
        gridContainerRef.current;
      // Show indicator if there's more to scroll (with small threshold for rounding)
      setCanScrollRight(scrollWidth - scrollLeft - clientWidth > 5);
      setCanScrollDown(scrollHeight - scrollTop - clientHeight > 5);
    }
  }, []);

  // The tooltip grows upward from just above its cell, and nothing bounds its
  // height - a plot map's column count is open-ended, and so is a grade metric's
  // bucket list. Near the top of the viewport it would simply be cut off, so
  // measure the rendered box and flip it below the cell when that reads better.
  //
  // "Fits above" is not enough on its own to decide: a tooltip taller than the
  // screen fits on neither side, and flipping it below a cell low in the viewport
  // pushes it off the bottom entirely, where staying above would at least have
  // shown most of it. So fall back to whichever side has more room.
  //
  // Identity only changes with `tooltipData`, so React re-invokes this once per
  // hover rather than on every placement change - no feedback loop.
  const measureTooltip = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !tooltipData) return;
      const spaceAbove = tooltipData.y - TOOLTIP_CELL_GAP;
      const spaceBelow = window.innerHeight - tooltipData.bottom - TOOLTIP_CELL_GAP;
      const fitsAbove = node.offsetHeight <= spaceAbove;
      setTooltipPlacement(fitsAbove || spaceAbove >= spaceBelow ? "above" : "below");
    },
    [tooltipData]
  );

  const {
    plots,
    loading: plotsLoading,
    error: plotsError,
    isCurrentSnapshot: isPlotsSnapshotCurrent,
  } = usePlots(trialId, experimentId);
  const exportTaskId = taskId ?? task.id;
  // Names for the export's export_info.json; it keeps them only when they belong to the verified task.
  const exportLabels = { trial, organization: selectedOrganization };
  const entryCollectionsSnapshot = useEntryCollections(trialId, experimentId, exportTaskId);
  const {
    entryCollections,
    loading: entryCollectionsLoading,
    error: entryCollectionsError,
    isCurrentSnapshot: isEntryCollectionsSnapshotCurrent,
  } = entryCollectionsSnapshot;
  const { plotData, loading: dataLoading, error: dataError } = usePlotData(
    trialId,
    experimentId,
    exportTaskId,
    plots,
    entryCollectionsSnapshot
  );
  const cocoExportSource = useMemo(
    () =>
      resolveCocoExportSourceFromSnapshots({
        organizationId: selectedOrganization?.id,
        trialId,
        experimentId,
        taskId: exportTaskId,
        trial,
        experiment,
        task,
      }),
    [selectedOrganization?.id, trialId, experimentId, exportTaskId, trial, experiment, task]
  );
  // Older documents can lack orgId and still support CSV-only export, but a
  // known non-matching org means these are retained snapshots from another
  // selected organization and must never be combined with current listeners.
  const loadedDocumentsMatchSelectedOrganization =
    !selectedOrganization?.id ||
    ((!trial?.orgId || trial.orgId === selectedOrganization.id) &&
      (!experiment?.orgId || experiment.orgId === selectedOrganization.id));

  // Get the currently selected entry config
  const selectedEntryConfig = useMemo(() => {
    return availableEntryConfigs.find((config) => config.id === selectedEntryConfigId);
  }, [availableEntryConfigs, selectedEntryConfigId]);

  const grading = useMemo(() => {
    if (selectedEntryConfig?.type !== "imageMeasurement") return undefined;
    return selectedEntryConfig.imageMeasurementConfig?.grading;
  }, [selectedEntryConfig]);

  const isGradeMetric =
    measurementMetric === "grade" &&
    !!grading &&
    grading.buckets.length > 0;

  const [gradeMetadataByPath, setGradeMetadataByPath] = useState<Map<string, CaptureMetadata>>(
    () => new Map()
  );
  const [gradeMetadataLoading, setGradeMetadataLoading] = useState(false);

  const gradeMetadataTargets = useMemo(() => {
    if (!isGradeMetric || !selectedEntryConfigId) return [];
    const targets = new Map<string, GradeMetadataTarget>();
    for (const d of plotData) {
      for (const collection of d.entryCollections) {
        const collectionConfigId =
          typeof collection.entryConfigRef === "string"
            ? collection.entryConfigRef
            : collection.entryConfigRef.id;
        if (
          collectionConfigId !== selectedEntryConfigId ||
          collection.type !== "imageMeasurement"
        )
          continue;
        const entries = collection.imageMeasurementEntries
          ? Object.values(collection.imageMeasurementEntries)
          : [];
        for (const entry of entries) {
          if (!entry?.captureMetadataCloudPath) continue;
          const version = getGradeMetadataTargetVersion(entry.updatedAt);
          targets.set(`${entry.captureMetadataCloudPath}\u0000${version}`, {
            path: entry.captureMetadataCloudPath,
            updatedAt: entry.updatedAt,
          });
        }
      }
    }
    return Array.from(targets.values()).sort(
      (a, b) =>
        a.path.localeCompare(b.path) ||
        getGradeMetadataTargetVersion(a.updatedAt).localeCompare(
          getGradeMetadataTargetVersion(b.updatedAt)
        )
    );
  }, [isGradeMetric, selectedEntryConfigId, plotData]);

  useEffect(() => {
    if (!isGradeMetric || gradeMetadataTargets.length === 0) {
      setGradeMetadataByPath(new Map());
      setGradeMetadataLoading(false);
      return;
    }

    let cancelled = false;
    setGradeMetadataLoading(true);
    fetchGradeMetadataByPath(gradeMetadataTargets).then((next) => {
      if (cancelled) return;
      setGradeMetadataByPath(next);
      setGradeMetadataLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isGradeMetric, gradeMetadataTargets]);

  // Precompute per-plot grade distributions when grade mode is active.
  const gradeDistributionsByPlot = useMemo(() => {
    const result = new Map<string, GradeDistribution>();
    if (!isGradeMetric || !grading) return result;
    for (const d of plotData) {
      const metadataList: CaptureMetadata[] = [];
      for (const collection of d.entryCollections) {
        const collectionConfigId =
          typeof collection.entryConfigRef === "string"
            ? collection.entryConfigRef
            : collection.entryConfigRef.id;
        if (
          collectionConfigId !== selectedEntryConfigId ||
          collection.type !== "imageMeasurement"
        )
          continue;
        const entries = collection.imageMeasurementEntries
          ? Object.values(collection.imageMeasurementEntries)
          : [];
        for (const entry of entries) {
          const path = entry?.captureMetadataCloudPath;
          if (!path) continue;
          const metadata = gradeMetadataByPath.get(path);
          if (metadata) metadataList.push(metadata);
        }
      }
      if (metadataList.length === 0) continue;
      const dist = computeGradeDistribution(metadataList, grading);
      if (dist) result.set(d.plot.plotID, dist);
    }
    return result;
  }, [isGradeMetric, grading, plotData, selectedEntryConfigId, gradeMetadataByPath]);

  const deferredAnalysisSummary = useMemo(
    () => summarizeDeferredAnalysisForTask(entryCollections, availableEntryConfigs),
    [availableEntryConfigs, entryCollections]
  );

  const deferredAnalysisTaskPath = useMemo(
    () =>
      selectedOrganization?.id && trialId && experimentId && task.id
        ? `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${experimentId}/tasks/${task.id}`
        : null,
    [experimentId, selectedOrganization?.id, task.id, trialId]
  );

  const deferredAnalysisJobId = useMemo(
    () =>
      trialId && experimentId && task.id
        ? getDeferredAnalysisJobId({ trialId, experimentId, taskId: task.id, scope: "task" })
        : null,
    [experimentId, task.id, trialId]
  );

  useEffect(() => {
    if (!selectedOrganization?.id || !deferredAnalysisJobId || !isDeferredAnalysisUiEnabled()) {
      setDeferredAnalysisJob(null);
      setIsDeferredAnalysisJobLoading(false);
      return undefined;
    }

    setIsDeferredAnalysisJobLoading(true);
    return subscribeToDeferredAnalysisJob(
      selectedOrganization.id,
      deferredAnalysisJobId,
      (job) => {
        setIsDeferredAnalysisJobLoading(false);
        setDeferredAnalysisJob((currentJob) => {
          if (
            job &&
            currentJob?.id === job.id &&
            currentJob.runId &&
            job.runId &&
            currentJob.runId !== job.runId &&
            isDeferredAnalysisJobSnapshotActive(currentJob) &&
            !isDeferredAnalysisJobSnapshotActive(job)
          ) {
            return currentJob;
          }
          return job;
        });
      },
      (error) => {
        setIsDeferredAnalysisJobLoading(false);
        console.error("Deferred analysis job subscription failed:", error);
        toast({
          title: "Could not watch analysis progress",
          description: "The analysis job may still be running. Refresh the page to check its latest status.",
          variant: "destructive",
        });
      }
    );
  }, [deferredAnalysisJobId, selectedOrganization?.id, toast]);

  const isDeferredAnalysisJobActive = isDeferredAnalysisJobSnapshotActive(deferredAnalysisJob);
  const isDeferredAnalysisProcessing = isDeferredAnalysisJobActive || deferredAnalysisSummary.processingCount > 0;
  const isDeferredAnalysisJobPendingCheck = isDeferredAnalysisJobLoading && deferredAnalysisSummary.eligibleCount > 0;
  const isDeferredAnalysisButtonBusy =
    isStartingDeferredAnalysis || isDeferredAnalysisProcessing || isDeferredAnalysisJobPendingCheck;

  const showDeferredAnalysisButton =
    isDeferredAnalysisUiEnabled() &&
    deferredAnalysisSummary.supported &&
    (isSuperAdmin || userRole === "editor" || userRole === "admin") &&
    (deferredAnalysisSummary.eligibleCount > 0 || isDeferredAnalysisProcessing || isDeferredAnalysisJobPendingCheck);

  const deferredAnalysisButtonLabel = (() => {
    if (isDeferredAnalysisProcessing) return "Analysis Running";
    if (isDeferredAnalysisJobPendingCheck) return "Checking Analysis";
    if (
      deferredAnalysisSummary.pendingCount === 0 &&
      deferredAnalysisSummary.staleProcessingCount === 0 &&
      deferredAnalysisSummary.failedCount > 0
    ) {
      return "Retry Failed Analysis";
    }
    return "Run Pending Analysis";
  })();
  const deferredAnalysisProgressText = getDeferredAnalysisJobProgressText(deferredAnalysisJob);

  // ─── Persistent export-job subscription ───
  // Mirrors the deferred-analysis pattern: deterministic jobId from route
  // params + onSnapshot so the user can navigate away/close the tab and pick
  // the live progress back up on next mount.
  const [exportJob, setExportJob] = useState<ExportJobSnapshot | null>(null);
  const [isStartingExport, setIsStartingExport] = useState(false);
  const lastAutoDownloadedRef = useRef<string | null>(null);
  const [dialogMode, setDialogMode] = useState<"auto" | "options">("auto");
  // runIds that this mount initiated via `startExportJob`. Only auto-download
  // when the completing job's runId is in this set — otherwise:
  //  - a coworker's active export visible to this tab (same org, same task)
  //    would auto-download in this user's browser, and
  //  - cold-mounting on an already-completed doc would re-download on every
  //    page visit.
  // After the page reloads or unmounts, the set resets; on next mount the
  // user sees "Download Ready" and uses the dialog's "Download existing"
  // button instead of being surprised by an automatic download.
  //
  // State (not a ref) so that adding a runId after `startExportJob` resolves
  // triggers the auto-download effect to re-evaluate — covering the race
  // where a very fast worker completes before the start callable returns.
  const [ownedExportRunIds, setOwnedExportRunIds] = useState<Set<string>>(() => new Set());
  // Forces a re-render once a minute while an export job is active so
  // `isExportStalled` (which depends on `Date.now()`) flips to true after the
  // stale window even if no progress writes arrive to trigger a re-render.
  const [, setStaleCheckTick] = useState(0);

  const exportJobId = useMemo(
    () =>
      trialId && experimentId && task.id
        ? getExportJobId({ trialId, experimentId, taskId: task.id })
        : null,
    [experimentId, task.id, trialId]
  );

  useEffect(() => {
    if (!selectedOrganization?.id || !exportJobId) {
      setExportJob(null);
      return undefined;
    }
    return subscribeToExportJob(
      selectedOrganization.id,
      exportJobId,
      (job) => setExportJob(job),
      (error) => {
        // Match the deferred-analysis listener nearby: clear local state and
        // toast so the user has a concrete next step instead of a button
        // that silently sticks on "Export Running".
        console.error("Export job subscription failed:", error);
        setExportJob(null);
        toast({
          title: "Could not watch export progress",
          description:
            "The export may still be running. Refresh the page to check its latest status.",
          variant: "destructive",
        });
      }
    );
  }, [exportJobId, selectedOrganization?.id, toast]);

  const isExportJobActive = isExportJobSnapshotActive(exportJob);
  useEffect(() => {
    if (!isExportJobActive) return undefined;
    const id = setInterval(() => setStaleCheckTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [isExportJobActive]);
  const exportJobProgressText = getExportJobProgressText(exportJob);
  // One clock read per render, shared by every time-based export flag below.
  // The `staleCheckTick` interval above forces a re-render while a job is
  // active so these recompute; the 24h expiry boundary needs no finer cadence.
  const nowMs = Date.now();
  // Best-effort, NOT the authority: catches the common case where the storage
  // lifecycle has deleted (or will imminently delete) the ZIP once it is past
  // `expiresAt`. It cannot see an object deleted while `expiresAt` is still in
  // the future — the server's object-existence probe in `getExportDownloadUrl`
  // remains the source of truth, and `handleDownloadExisting` degrades
  // gracefully on its failed-precondition error.
  const isExportExpired = isExportJobExpired(exportJob, nowMs);
  const hasRecentCompletedExport =
    exportJob?.status === "completed" &&
    exportJob.active === false &&
    Boolean(exportJob.filename) &&
    !isExportExpired;
  const hasRecentFailedExport = exportJob?.status === "failed";
  // A previous export that has expired (server flipped it to `expired`, or its
  // 24h `expiresAt` has passed locally). Mirrors `hasRecentCompletedExport` as
  // a pure terminal-state check; the dialog guards (`!active`, `!failed`) live
  // in `showExpiredView`, parallel to the other view flags.
  const hasExpiredPreviousExport = isExportExpired && Boolean(exportJob?.filename);
  // Active export whose `updatedAt` is past the stale-running window — the
  // watchdog missed (crash/OOM/network) and the job is stuck. UI surfaces a
  // Retry affordance so the user has an explicit recovery path.
  const isExportStalled = isExportJobLikelyStuck(exportJob, nowMs);
  // Treat the cloud-function path as "busy" between the click and the job
  // reaching a terminal state. The CSV-only / client-side JSZip path uses
  // `isDownloading` instead (see handleDownload).
  const isExportRunning = isStartingExport || isExportJobActive;

  // Extract field-added trait IDs (traits with userVisibility set)
  const fieldAddedTraitIds = useMemo(() => {
    return new Set(
      (task.dataCollectionConfigs || [])
        .filter((config) => config.userVisibility && config.userVisibility.length > 0)
        .map((config) => (typeof config.entryConfigRef === "string" ? config.entryConfigRef : config.entryConfigRef.id))
    );
  }, [task.dataCollectionConfigs]);

  // Determine display mode based on percentage of plots with valid row/column data
  // Use grid mode if >50% of plots have positioning data, otherwise use list mode
  const displayMode: DisplayMode = useMemo(() => {
    if (plots.length === 0) return "grid"; // Default to grid if no plots
    const plotsWithGrid = plots.filter((p) => p.row !== undefined && p.column !== undefined);
    const gridPercentage = plotsWithGrid.length / plots.length;
    return gridPercentage > 0.5 ? "grid" : "list";
  }, [plots]);

  // Sort plots for list view by plotMapUploadPosition, then plotID
  const sortedPlotsForListView = useMemo(() => {
    if (displayMode !== "list") return [];
    return [...plots].sort((a, b) => {
      // Primary sort: plotMapUploadPosition (ascending, undefined goes last)
      const posA = a.plotMapUploadPosition ?? Number.MAX_SAFE_INTEGER;
      const posB = b.plotMapUploadPosition ?? Number.MAX_SAFE_INTEGER;
      if (posA !== posB) return posA - posB;
      // Fallback sort: plotID (alphanumeric)
      return a.plotID.localeCompare(b.plotID, undefined, { numeric: true });
    });
  }, [plots, displayMode]);

  // Assign virtual row/col positions to sorted plots for list view rendering
  const listViewGridPositions = useMemo(() => {
    if (displayMode !== "list") return new Map<string, { row: number; col: number }>();
    const positions = new Map<string, { row: number; col: number }>();
    sortedPlotsForListView.forEach((plot, index) => {
      positions.set(plot.plotID, {
        row: Math.floor(index / LIST_VIEW_MAX_COLUMNS) + 1,
        col: (index % LIST_VIEW_MAX_COLUMNS) + 1,
      });
    });
    return positions;
  }, [sortedPlotsForListView, displayMode]);

  // Calculate list view dimensions
  const listViewDimensions = useMemo(() => {
    if (displayMode !== "list") return { rows: 0, cols: 0 };
    const totalPlots = sortedPlotsForListView.length;
    return {
      rows: Math.ceil(totalPlots / LIST_VIEW_MAX_COLUMNS),
      cols: Math.min(totalPlots, LIST_VIEW_MAX_COLUMNS),
    };
  }, [sortedPlotsForListView, displayMode]);

  // Transform sparse plot data into D3-friendly format
  const heatmapData: HeatmapDataPoint[] = useMemo(() => {
    if (!selectedEntryConfigId || plotData.length === 0) return [];

    // Check if measurement metric is required for this entry type
    const requiresMetric =
      selectedEntryConfig?.type === "imageMeasurement" ||
      selectedEntryConfig?.type === "standCountMeasurement" ||
      selectedEntryConfig?.type === "rowPlantedStandCount";
    if (requiresMetric && !measurementMetric) return [];

    // Separate image measurement, stand count, and row planted stand count metrics
    const imageMeasurementMetric: ImageMeasurementMetric | undefined =
      selectedEntryConfig?.type === "imageMeasurement" ? (measurementMetric as ImageMeasurementMetric) : undefined;
    const standCountMetric: StandCountMetric | undefined =
      selectedEntryConfig?.type === "standCountMeasurement" ? (measurementMetric as StandCountMetric) : undefined;
    const rowPlantedStandCountMetric: RowPlantedStandCountMetric | undefined =
      selectedEntryConfig?.type === "rowPlantedStandCount"
        ? (measurementMetric as RowPlantedStandCountMetric)
        : undefined;

    // Filter plots based on display mode
    const plotsToRender =
      displayMode === "grid"
        ? plotData.filter((d) => d.plot.row !== undefined && d.plot.column !== undefined)
        : plotData; // List view includes all plots

    return plotsToRender
      .map((d) => {
        // Use getEntryDisplayValueWithStatus to get error state for weighted average cases
        const result = getEntryDisplayValueWithStatus(
          d.entryCollections,
          selectedEntryConfigId,
          imageMeasurementMetric,
          standCountMetric,
          rowPlantedStandCountMetric,
          selectedEntryConfig
        );
        const collectedEntryCount = getCollectedEntryCount(d.entryCollections, selectedEntryConfigId);
        const analysisStatus = getDeferredAnalysisPlotStatus(
          d.entryCollections,
          selectedEntryConfigId,
          selectedEntryConfig
        );

        // Determine row/col based on display mode
        let row: number, col: number;
        if (displayMode === "grid") {
          row = d.plot.row!;
          col = d.plot.column!;
        } else {
          // List mode: use virtual positions
          const virtualPos = listViewGridPositions.get(d.plot.plotID);
          row = virtualPos?.row ?? 1;
          col = virtualPos?.col ?? 1;
        }

        // For grade mode, the displayed value is the dominant bucket label and
        // the underlying distribution rides along for the tooltip.
        let displayValue: number | string | null = result.value;
        let gradeDistribution: GradeDistribution | undefined;
        if (isGradeMetric) {
          gradeDistribution = gradeDistributionsByPlot.get(d.plot.plotID);
          displayValue = gradeDistribution?.dominantLabel ?? null;
        }

        const hasDisplayValue = displayValue !== null && displayValue !== undefined;
        const isCollectedEmpty = !hasDisplayValue && collectedEntryCount > 0;

        return {
          row,
          col,
          plotId: d.plot.plotID,
          value: displayValue,
          rawData: d,
          hasError: result.hasError,
          errorReason: result.errorReason,
          analysisStatus: analysisStatus?.status,
          analysisError: analysisStatus?.error,
          analysisUnreviewed: analysisStatus?.unreviewed,
          collectedEntryCount,
          isCollectedEmpty,
          gradeDistribution,
        };
      })
      .filter(
        (d) =>
          (d.value !== null && d.value !== undefined) ||
          d.hasError ||
          d.analysisStatus ||
          d.analysisUnreviewed ||
          d.isCollectedEmpty
      );
  }, [
    plotData,
    selectedEntryConfigId,
    measurementMetric,
    selectedEntryConfig,
    displayMode,
    listViewGridPositions,
    isGradeMetric,
    gradeDistributionsByPlot,
  ]);

  const hasUnreviewedDeferredResults = heatmapData.some((d) => d.analysisUnreviewed);

  // Calculate grid dimensions based on display mode
  const { maxRows, maxCols, minRows, minCols } = useMemo(() => {
    if (plots.length === 0) return { maxRows: 0, maxCols: 0, minRows: 0, minCols: 0 };

    // In list mode, use the calculated list view dimensions
    if (displayMode === "list") {
      return {
        maxRows: listViewDimensions.rows,
        maxCols: listViewDimensions.cols,
        minRows: 1,
        minCols: 1,
      };
    }

    // In grid mode, calculate from actual plot positions
    const rows = plots.map((p) => p.row).filter((r) => r !== undefined) as number[];
    const cols = plots.map((p) => p.column).filter((c) => c !== undefined) as number[];

    // Handle case where no plots have valid positions
    if (rows.length === 0 || cols.length === 0) {
      return { maxRows: 0, maxCols: 0, minRows: 0, minCols: 0 };
    }

    return {
      maxRows: Math.max(...rows),
      maxCols: Math.max(...cols),
      minRows: Math.min(...rows),
      minCols: Math.min(...cols),
    };
  }, [plots, displayMode, listViewDimensions]);

  // Create D3 scales
  const { xScale, yScale, colorScale, actualWidth, actualHeight, margin } = useMemo(() => {
    const margin = { top: 0, right: 0, bottom: 0, left: 0 };

    // Create array of all possible column and row values
    const colDomain = Array.from({ length: maxCols - minCols + 1 }, (_, i) => minCols + i);
    const rowDomain = Array.from({ length: maxRows - minRows + 1 }, (_, i) => minRows + i);

    const actualWidth = colDomain.length * GRID_CELL_SIZE;
    const actualHeight = rowDomain.length * GRID_CELL_SIZE;

    const xScale = d3.scaleBand().domain(colDomain.map(String)).range([0, actualWidth]).padding(0);

    const yScale = d3.scaleBand().domain(rowDomain.map(String)).range([0, actualHeight]).padding(0);

    // Create color scale based on entry config type
    const selectedEntryConfig = availableEntryConfigs.find((config) => config.id === selectedEntryConfigId);
    let colorScale: (value: string | number) => string;

    // For text-based data types, use light background for readability
    const isTextBased =
      selectedEntryConfig?.type === "text" ||
      selectedEntryConfig?.type === "category" ||
      selectedEntryConfig?.type === "boolean" ||
      selectedEntryConfig?.type === "transcription" ||
      selectedEntryConfig?.type === "photo";

    if (isGradeMetric && grading) {
      // Categorical bucket palette — domain follows config order so colors are
      // stable across plots and match the tooltip's legend.
      const palette =
        grading.buckets.length <= 8 ? d3.schemeSet2 : d3.schemeTableau10;
      const ordinal = d3
        .scaleOrdinal<string, string>()
        .domain(grading.buckets.map((b) => b.label))
        .range(palette as readonly string[]);
      colorScale = (value: string | number) => {
        if (value === null || value === undefined) return "#f5f5f5";
        return ordinal(String(value)) ?? "#f5f5f5";
      };
    } else if (isTextBased) {
      // Use subtle, light colors that don't interfere with text readability
      if (selectedEntryConfig?.type === "category" && selectedEntryConfig.categoryConfig?.choices) {
        // Light pastel colors for categories with good contrast
        const lightColors = ["#e3f2fd", "#f3e5f5", "#e8f5e9", "#fff3e0", "#fce4ec", "#e0f2f1", "#f1f8e9", "#ede7f6"];
        const ordinalScale = d3.scaleOrdinal(lightColors).domain(selectedEntryConfig.categoryConfig.choices);
        // Handle both single values and arrays (for multi-select) - use first selection for coloring
        colorScale = (value: string | number | string[]) => {
          const firstValue = Array.isArray(value) ? value[0] : String(value);
          return ordinalScale(firstValue);
        };
      } else if (selectedEntryConfig?.type === "boolean") {
        // Green for true/yes, red for false/no
        colorScale = (value: string | number) => {
          const boolValue = typeof value === "boolean" ? value : value === "true" || value === "Yes";
          return boolValue ? "#e8f5e9" : "#ffebee";
        };
      } else {
        // Very light gray for text fields
        colorScale = () => "#f5f5f5";
      }
    } else {
      // For numeric and date data, use heat map colors with lighter range for readability
      // Convert values to numbers (dates become timestamps)
      // Filter out error cells (hasError with null value) to avoid skewing the color scale
      const numericValues = heatmapData
        .filter((d) => !d.hasError && d.value !== null && d.value !== undefined)
        .map((d) => {
          const val = d.value;
          // Convert Firebase Timestamp to numeric
          if (val && typeof val === "object" && "seconds" in val) {
            return val.seconds * 1000;
          }
          return Number(val);
        });

      const extent = d3.extent(numericValues) as [number, number];
      // Use a custom interpolator that stops before the darkest colors
      const customInterpolator = (t: number) => d3.interpolateYlOrRd(t * 0.75);
      const sequentialScale = d3
        .scaleSequential(customInterpolator)
        .domain(extent.every((v) => v !== undefined) ? extent : [0, 1]);

      colorScale = (value: string | number) => {
        // Convert value to number if it's a Firebase Timestamp
        let numValue: number;
        if (isTimestampLike(value)) {
          numValue = value.seconds * 1000;
        } else {
          numValue = Number(value);
        }
        return sequentialScale(numValue);
      };
    }

    return { xScale, yScale, colorScale, actualWidth, actualHeight, margin };
  }, [
    heatmapData,
    availableEntryConfigs,
    selectedEntryConfigId,
    maxRows,
    maxCols,
    minRows,
    minCols,
    isGradeMetric,
    grading,
  ]);
  const gridWidth = GRID_CELL_SIZE + actualWidth;

  // Update dimensions on resize and check scroll state
  useEffect(() => {
    const updateDimensions = () => {
      if (gridContainerRef.current) {
        const { width } = gridContainerRef.current.getBoundingClientRect();
        setDimensions((prev) => ({ ...prev, width: Math.max(400, width - 40) }));
      }
      updateScrollIndicator();
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [updateScrollIndicator]);

  // Update scroll indicator when data changes
  useEffect(() => {
    updateScrollIndicator();
  }, [heatmapData, updateScrollIndicator]);

  // Handle click
  const handleClick = (data: HeatmapDataPoint) => {
    onPlotSelect?.(data.rawData.plot, data.rawData.entryCollections);
  };

  // Trigger browser download from a URL (works for both object URLs and signed URLs)
  const triggerDownload = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle parent media checkbox: all on or all off
  const handleParentMediaToggle = (checked: boolean) => {
    setMediaOptions(checked ? ALL_MEDIA_OPTIONS : NO_MEDIA_OPTIONS);
  };

  // Toggle individual media option
  const handleMediaOptionToggle = (key: keyof MediaDownloadOptions, checked: boolean) => {
    setMediaOptions((prev) => ({ ...prev, [key]: checked }));
  };

  // Server-side download path. Kicks off `startExportJob` (persistent worker)
  // and returns. The live status + auto-download is driven by the export-job
  // subscription effect below.
  const downloadViaCloudFunction = async () => {
    if (
      !isPlotsSnapshotCurrent ||
      !loadedDocumentsMatchSelectedOrganization ||
      !isEntryCollectionsSnapshotCurrent ||
      entryCollectionsLoading ||
      entryCollectionsError
    ) {
      throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
    }
    if (mediaOptions.includeOriginalImages && !cocoExportSource) {
      throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
    }
    setIsStartingExport(true);
    try {
      const response = await generateTaskZipViaCloudFunction({
        data: {
          experiment: experiment!,
          plots,
          entryCollections,
          entryConfigs: availableEntryConfigs,
          ...exportLabels,
        },
        trialId,
        experimentId,
        taskId: task.id,
        cocoExportSource,
        taskName: task.name,
        taskStartDate: task.startDate,
        mediaOptions,
        onProgress: setDownloadProgress,
        shouldCancel: () => cancelDownloadRef.current,
      });
      // Only claim ownership of runs we *freshly* started. When
      // `reusedExistingJob` is true, the active export was started by another
      // tab/user with potentially-different media options (the toast just
      // told the user as much), and auto-downloading that ZIP here would
      // hand them a file that doesn't match what they selected.
      //
      // Add to state (not ref) so the auto-download effect re-evaluates —
      // covers the race where a fast worker completes before this callable
      // returns.
      if (!response.reusedExistingJob) {
        setOwnedExportRunIds((prev) => {
          if (prev.has(response.runId)) return prev;
          const next = new Set(prev);
          next.add(response.runId);
          return next;
        });
      }
      const optimistic = buildOptimisticExportJobSnapshot({
        jobId: response.jobId,
        runId: response.runId,
        status: "initializing",
        reusedExistingJob: response.reusedExistingJob,
      });
      if (optimistic) {
        // Don't downgrade a terminal snapshot of the same run: a very fast
        // worker may have written `completed`/`failed`/`expired` and fired
        // the onSnapshot callback before this callable resolved.
        setExportJob((current) => {
          if (
            current &&
            current.runId === optimistic.runId &&
            (current.status === "completed" ||
              current.status === "failed" ||
              current.status === "expired")
          ) {
            return current;
          }
          return optimistic;
        });
      }
      toast({
        title: response.reusedExistingJob ? "Export already running" : "Export started",
        description: response.reusedExistingJob
          ? "An export is already in progress for this task — your selected options were not applied. Wait for it to finish, then click Download again to start a new export with your options."
          : "Building your ZIP in the background. Watch the Download button for progress. If you leave the page, click Download when you return to grab your file.",
      });
    } finally {
      setIsStartingExport(false);
      setIsDownloading(false);
      setDownloadProgress(null);
      setIsDownloadDialogOpen(false);
      setDialogMode("auto");
    }
  };

  // Auto-download on completion (guarded so re-renders don't re-fire). Depend
  // only on the fields the effect actually reads — `exportJob` itself changes
  // on every progress write, which would otherwise re-fire the effect body
  // (cheap due to the ref guard, but wasted work).
  const exportJobStatus = exportJob?.status;
  const exportJobRunId = exportJob?.runId;
  useEffect(() => {
    if (exportJobStatus !== "completed") return;
    if (!selectedOrganization?.id || !exportJobId) return;
    // Only auto-download runs this tab originated. Prevents a coworker's
    // export from triggering an unsolicited download in every other org
    // member's open task tab, and prevents re-downloading on cold mount
    // when the doc happens to already be completed.
    if (!exportJobRunId || !ownedExportRunIds.has(exportJobRunId)) return;
    const fingerprint = `${exportJobId}:${exportJobRunId}`;
    if (lastAutoDownloadedRef.current === fingerprint) return;
    lastAutoDownloadedRef.current = fingerprint;

    void (async () => {
      try {
        const { downloadUrl, filename } = await getExportDownloadUrl({
          orgId: selectedOrganization.id,
          jobId: exportJobId,
        });
        triggerDownload(downloadUrl, filename);
      } catch (error) {
        console.error("Failed to auto-trigger export download:", error);
        toast({
          title: "Export ready, but the download didn't start",
          description: "Click Download again to retry.",
          variant: "destructive",
        });
      }
    })();
  }, [exportJobStatus, exportJobRunId, exportJobId, ownedExportRunIds, selectedOrganization?.id, toast]);

  // Manual click on "Use existing" or the inline Download button when the
  // most recent job is already completed.
  const handleDownloadExisting = async () => {
    if (!selectedOrganization?.id || !exportJobId) return;
    try {
      const { downloadUrl, filename } = await getExportDownloadUrl({
        orgId: selectedOrganization.id,
        jobId: exportJobId,
      });
      triggerDownload(downloadUrl, filename);
      setIsDownloadDialogOpen(false);
      setDialogMode("auto");
    } catch (error) {
      console.error("Failed to fetch existing export URL:", error);
      // Only fall through to the options picker when the server tells us the
      // export really is gone (`failed-precondition` from getExportDownloadUrl
      // — emitted for `expired`, missing GCS object, or terminal-but-not-
      // completed status). Other error codes — network blips, auth refresh,
      // internal errors — should keep the existing-download retry path so we
      // don't trick the user into kicking off an expensive re-export.
      const code = (error as { code?: string } | null | undefined)?.code ?? "";
      const isMissing = code === "failed-precondition" || code === "functions/failed-precondition";
      if (isMissing) {
        setDialogMode("options");
        toast({
          title: "Previous export is no longer available",
          description: "Start a new export below.",
        });
      } else {
        toast({
          title: "Couldn't fetch the existing export",
          description: "Try clicking Download again. If it keeps failing, click Create new export.",
          variant: "destructive",
        });
      }
    }
  };

  // Handle download
  const handleDownload = async () => {
    try {
      if (!experiment) {
        console.error("No experiment data available");
        return;
      }
      if (
        !isPlotsSnapshotCurrent ||
        !loadedDocumentsMatchSelectedOrganization ||
        !isEntryCollectionsSnapshotCurrent ||
        entryCollectionsLoading ||
        entryCollectionsError
      ) {
        throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
      }
      if (mediaOptions.includeOriginalImages && !cocoExportSource) {
        throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
      }

      setIsDownloading(true);
      setDownloadProgress(null);
      cancelDownloadRef.current = false;

      // CSV-only: always use client-side path
      if (!hasAnyMediaOption(mediaOptions)) {
        const { blob, filename } = await generateTaskZip(
          { experiment, plots, entryCollections, entryConfigs: availableEntryConfigs, ...exportLabels },
          task.name,
          task.startDate,
          NO_MEDIA_OPTIONS,
          setDownloadProgress,
          () => cancelDownloadRef.current,
          cocoExportSource,
          { trialId, experimentId, taskId: exportTaskId }
        );
        downloadBlob(blob, filename);
        setIsDownloading(false);
        setDownloadProgress(null);
        setIsDownloadDialogOpen(false);
        return;
      }

      // Media included: check threshold for routing
      // Debug: set VITE_FORCE_SERVER_DOWNLOAD=true to always use Cloud Function path
      const forceServerDownload = import.meta.env.VITE_FORCE_SERVER_DOWNLOAD === "true";
      const mediaCount = countMediaFiles(entryCollections, mediaOptions);

      if (forceServerDownload || mediaCount > MEDIA_FILE_THRESHOLD) {
        // Large download → route to Cloud Function
        setDownloadProgress({ phase: "server", current: 0, total: 1, message: "Preparing export..." });
        await downloadViaCloudFunction();
        // downloadViaCloudFunction handles cleanup and shows confirmation dialog
        return;
      }

      // Small download → try client-side, fallback to server on RangeError
      try {
        const { blob, filename } = await generateTaskZip(
          { experiment, plots, entryCollections, entryConfigs: availableEntryConfigs, ...exportLabels },
          task.name,
          task.startDate,
          mediaOptions,
          setDownloadProgress,
          () => cancelDownloadRef.current,
          cocoExportSource,
          { trialId, experimentId, taskId: exportTaskId }
        );
        downloadBlob(blob, filename);
      } catch (clientError: unknown) {
        // Detect ArrayBuffer allocation failure (Chrome ~2.15 GB limit)
        if (clientError instanceof RangeError && clientError.message.includes("allocation")) {
          console.warn("Client-side ZIP failed with RangeError, falling back to server:", clientError.message);
          setDownloadProgress({
            phase: "fallback",
            current: 0,
            total: 1,
            message: "File set too large for browser, retrying...",
          });
          await downloadViaCloudFunction();
          // downloadViaCloudFunction handles cleanup and shows confirmation dialog
          return;
        }
        throw clientError;
      }

      setIsDownloading(false);
      setDownloadProgress(null);
      setIsDownloadDialogOpen(false);
    } catch (error: unknown) {
      if (getErrorMessage(error) === "Download cancelled" || getErrorName(error) === "AbortError") {
        console.log("Download cancelled by user");
      } else {
        console.error("Error generating ZIP:", error);
        toast({
          title: "Export failed",
          description: getErrorMessage(error) || "Couldn't create the ZIP. Try again.",
          variant: "destructive",
        });
      }
      setIsDownloading(false);
      setDownloadProgress(null);
      setIsDownloadDialogOpen(false);
    }
  };

  const handleRunDeferredAnalysis = async () => {
    if (!deferredAnalysisTaskPath) {
      toast({
        title: "Pending analysis unavailable",
        description: "Task context is still loading.",
        variant: "destructive",
      });
      return;
    }

    setIsStartingDeferredAnalysis(true);
    try {
      const result = await startDeferredAnalysisJob({
        taskPath: deferredAnalysisTaskPath,
        scope: "task",
        includeFailed: true,
      });
      const optimisticJob = buildOptimisticDeferredAnalysisJobSnapshot(result);
      if (optimisticJob) {
        setDeferredAnalysisJob((currentJob) =>
          currentJob?.id === optimisticJob.id && isDeferredAnalysisJobSnapshotActive(currentJob)
            ? currentJob
            : optimisticJob
        );
      }

      toast({
        title: result.reusedExistingJob ? "Analysis already running" : "Pending analysis started",
        description:
          result.totalCount > 0
            ? `${result.totalCount} capture${result.totalCount === 1 ? "" : "s"} queued for analysis.`
            : "No uploaded pending or failed captures are available.",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not run pending analysis.";
      toast({
        title: "Pending analysis failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsStartingDeferredAnalysis(false);
    }
  };

  // Cancel only applies to the in-process client-side JSZip path (CSV-only
  // and small-media). The persistent cloud-function path can't be cancelled
  // mid-run in v1 — once started, the worker runs to completion.
  const handleCancelDownload = () => {
    cancelDownloadRef.current = true;
  };

  // Download is only safe once plots/data subscriptions have resolved without error and there's something to export.
  const hasDataError = !!(plotsError || dataError || entryCollectionsError);
  const isExportDataReady =
    !plotsLoading &&
    isPlotsSnapshotCurrent &&
    !dataLoading &&
    !entryCollectionsLoading &&
    isEntryCollectionsSnapshotCurrent &&
    loadedDocumentsMatchSelectedOrganization &&
    !hasDataError &&
    !!experiment &&
    plots.length > 0;
  const isExportReady = isExportDataReady && (!mediaOptions.includeOriginalImages || !!cocoExportSource);
  const exportBlockedReason =
    plotsLoading ||
    !isPlotsSnapshotCurrent ||
    dataLoading ||
    entryCollectionsLoading ||
    !isEntryCollectionsSnapshotCurrent ||
    !loadedDocumentsMatchSelectedOrganization
      ? "Loading task data..."
      : hasDataError
        ? `Cannot export: ${plotsError?.message || dataError?.message || entryCollectionsError?.message || "failed to load task data"}.`
        : !experiment
          ? "Experiment data is still loading."
          : plots.length === 0
            ? "This task has no plots to export."
            : mediaOptions.includeOriginalImages && !cocoExportSource
              ? "Cannot export original images and COCO annotations: trial and experiment organization provenance is missing or inconsistent. Ask an administrator to backfill the organization IDs."
              : null;

  // Decide which view the dialog should render. `dialogMode === "options"`
  // forces the options picker (set when the user clicks "Create new export"
  // from the use-existing prompt). Otherwise the live export-job state
  // chooses between stalled, in-progress, failed, use-existing, and options.
  //
  // We deliberately key off `isExportJobActive` (the Firestore-backed state)
  // rather than `isExportRunning` (which also includes the transient
  // `isStartingExport` flag). Otherwise the dialog would switch to the
  // "you can leave the page" view *during* client-side CSV prep — before
  // any job doc has been written — and closing the tab then would lose the
  // export silently.
  //
  // View priority (first match wins):
  //   1. stalled — active job whose updatedAt is past the stale window
  //   2. in-progress — active job making fresh progress
  //   3. failed-banner-with-options — terminal failed job
  //   4. use-existing — terminal completed job still inside TTL
  //   5. expired — terminal export whose ZIP has been (or will be) deleted
  //   6. options picker (default)
  const showStalledView =
    dialogMode === "auto" && isExportJobActive && isExportStalled;
  const showInProgressView =
    dialogMode === "auto" && isExportJobActive && !isExportStalled;
  const showFailedView =
    dialogMode === "auto" && !isExportJobActive && hasRecentFailedExport;
  const showUseExistingView =
    dialogMode === "auto" &&
    !isExportJobActive &&
    !hasRecentFailedExport &&
    hasRecentCompletedExport;
  const showExpiredView =
    dialogMode === "auto" &&
    !isExportJobActive &&
    !hasRecentFailedExport &&
    hasExpiredPreviousExport;

  const completedAt = exportJob?.completedAt;
  const recentExportTimestamp = useMemo(() => {
    if (completedAt && typeof completedAt.toDate === "function") {
      return completedAt.toDate();
    }
    return null;
  }, [completedAt]);

  const downloadDialog = (
    <AlertDialog
      open={isDownloadDialogOpen}
      onOpenChange={(open) => {
        if (!isDownloading) {
          setIsDownloadDialogOpen(open);
          if (!open) setDialogMode("auto");
        }
      }}>
      <AlertDialogContent>
        {showStalledView ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Export appears stuck</AlertDialogTitle>
              <AlertDialogDescription>
                This export hasn't made progress for some time. The
                background worker may have failed silently (a crash,
                out-of-memory error, or a network blip).
                <span className="text-muted-foreground block pt-2">
                  Click Retry below to start a fresh export. If this keeps
                  happening, contact <strong>support@gleanag.com</strong>.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setIsDownloadDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  // Reuse the existing handler — server-side
                  // decideExportJobClaim accepts a fresh claim because the
                  // doc is stale-running.
                  try {
                    await downloadViaCloudFunction();
                  } catch (error: unknown) {
                    console.error("Retry of stalled export failed:", error);
                    toast({
                      title: "Could not start retry",
                      description:
                        getErrorMessage(error) ||
                        "Something went wrong starting a fresh export. Try again, or contact support@gleanag.com if it keeps happening.",
                      variant: "destructive",
                    });
                  }
                }}
                disabled={!isExportReady}>
                Retry
              </Button>
            </AlertDialogFooter>
          </>
        ) : showInProgressView ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Export in progress</AlertDialogTitle>
              <AlertDialogDescription>
                {exportJobProgressText ?? "Preparing your export..."}
                <span className="text-muted-foreground block pt-2">
                  You can close this dialog or leave the page. Click Download
                  when you return to grab your file.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button onClick={() => setIsDownloadDialogOpen(false)}>Hide</Button>
            </AlertDialogFooter>
          </>
        ) : showUseExistingView ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>A recent export is ready</AlertDialogTitle>
              <AlertDialogDescription>
                This trial was exported
                {recentExportTimestamp
                  ? ` on ${recentExportTimestamp.toLocaleString()} `
                  : " recently "}
                ({exportJob?.filename ?? "export.zip"}). You can download that
                file, or create a new export — do that if you've changed any
                data since.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setIsDownloadDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline" onClick={() => setDialogMode("options")}>
                Create new export
              </Button>
              <Button onClick={handleDownloadExisting}>Download existing</Button>
            </AlertDialogFooter>
          </>
        ) : showExpiredView ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>A previous export expired</AlertDialogTitle>
              <AlertDialogDescription>
                Your last export of this trial
                {recentExportTimestamp
                  ? ` (exported on ${recentExportTimestamp.toLocaleString()})`
                  : ""}{" "}
                has expired and is no longer available to download. Exports are
                kept for 24 hours. Create a new export to download the latest
                data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setIsDownloadDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setDialogMode("options")}>Create new export</Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            {showFailedView && (
              <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
                {exportJob?.failureKind === "timeout" ? (
                  <>
                    <p className="font-semibold text-destructive">Last export timed out</p>
                    <p className="mt-2 text-muted-foreground">
                      Your last export of this trial exceeded the time limit and was cancelled
                      before it finished. To get a successful export, try unchecking the media
                      types you don't need below:
                    </p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                      <li>
                        <strong>Images with AI annotations</strong> — slowest to produce; turn off
                        if you only need raw data.
                      </li>
                      <li>
                        <strong>Original images</strong> — turn off if you only need analysis
                        results.
                      </li>
                      <li>
                        <strong>Depth &amp; metadata</strong> — turn off if you don't need raw
                        sensor data.
                      </li>
                    </ul>
                    <p className="mt-2 text-muted-foreground">
                      If this keeps happening even with media unchecked, contact{" "}
                      <strong>support@gleanag.com</strong> — large trials may need a different
                      export pathway.
                    </p>
                  </>
                ) : exportJob?.failureKind === "capture-download" && exportJob.lastError ? (
                  <>
                    <p className="font-semibold text-destructive">Last export incomplete</p>
                    <p className="mt-2 text-muted-foreground">{exportJob.lastError}</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-destructive">Last export failed</p>
                    <p className="mt-2 text-muted-foreground">
                      Your last export of this trial failed unexpectedly. You can adjust the
                      media options below and try again. If this keeps happening, contact{" "}
                      <strong>support@gleanag.com</strong>.
                    </p>
                  </>
                )}
              </div>
            )}
            <AlertDialogHeader>
              <AlertDialogTitle>Download Task Data</AlertDialogTitle>
              <AlertDialogDescription>
                Download all collected data for this task as a ZIP file. The ZIP will include all media and CSVs
                containing collected data.
                <span className="mt-2 block">
                  Large exports will run in the background. Keep your browser open until this popup closes and then
                  the export will continue running.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className={cn("flex items-start space-x-2 pt-4", hasAnyMediaOption(mediaOptions) ? "pb-0" : "pb-4")}>
              <Checkbox
                id="include-media"
                checked={
                  hasAllMediaOptions(mediaOptions) ? true : hasAnyMediaOption(mediaOptions) ? "indeterminate" : false
                }
                onCheckedChange={(checked) => handleParentMediaToggle(checked === true)}
                disabled={isDownloading || !isExportDataReady}
                className="mt-0.5"
              />
              <label
                htmlFor="include-media"
                className="text-sm leading-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                <span className="font-medium">Include media files and annotations.</span>
              </label>
            </div>
            {hasAnyMediaOption(mediaOptions) && (
              <div className="ml-6 space-y-1.5 pb-4">
                {[
                  { key: "includeOriginalImages" as const, label: "Original images" },
                  { key: "includeAudio" as const, label: "Audio recordings" },
                  { key: "includeAnnotatedImages" as const, label: "Images with AI annotations" },
                  { key: "includeDepthAndMetadata" as const, label: "Depth & metadata" },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    className="flex items-center space-x-2">
                    <Checkbox
                      id={`media-${key}`}
                      checked={mediaOptions[key]}
                      onCheckedChange={(checked) => handleMediaOptionToggle(key, checked === true)}
                      disabled={isDownloading || !isExportDataReady}
                    />
                    <label
                      htmlFor={`media-${key}`}
                      className="text-sm">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            )}
            {exportBlockedReason && <p className="text-sm text-muted-foreground pb-2">{exportBlockedReason}</p>}
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={isDownloading ? handleCancelDownload : () => setIsDownloadDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                disabled={isDownloading || !isExportReady}>
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {downloadProgress?.phase === "preparing" && (downloadProgress.message || "Preparing data...")}
                    {downloadProgress?.phase === "downloading" &&
                      `Downloading ${downloadProgress.current}/${downloadProgress.total} files...`}
                    {downloadProgress?.phase === "compressing" && `Compressing... ${downloadProgress.current}%`}
                    {downloadProgress?.phase === "server" && (downloadProgress.message || "Starting export...")}
                    {downloadProgress?.phase === "fallback" && (downloadProgress.message || "Retrying...")}
                    {!downloadProgress && "Starting..."}
                  </>
                ) : (
                  "Download ZIP"
                )}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );

  // Get individual sample values for a plot
  const getSampleValues = (plotData: HeatmapDataPoint) => {
    // Timer is a numeric, multi-sample type with no metric selector, so it must
    // bypass the measurementMetric requirement to surface per-sample durations in
    // the tooltip number line (other non-metric types intentionally stay empty).
    const isTimerType = selectedEntryConfig?.type === "timer";
    if (!selectedEntryConfigId || (!measurementMetric && !isTimerType)) return [];

    // Separate image measurement, stand count, and row planted stand count metrics
    const imageMeasurementMetric: ImageMeasurementMetric | undefined =
      selectedEntryConfig?.type === "imageMeasurement" ? (measurementMetric as ImageMeasurementMetric) : undefined;
    const standCountMetric: StandCountMetric | undefined =
      selectedEntryConfig?.type === "standCountMeasurement" ? (measurementMetric as StandCountMetric) : undefined;
    const rowPlantedStandCountMetric: RowPlantedStandCountMetric | undefined =
      selectedEntryConfig?.type === "rowPlantedStandCount"
        ? (measurementMetric as RowPlantedStandCountMetric)
        : undefined;

    // Filter to only entry collections that match the selected entry config
    const matchingCollections = plotData.rawData.entryCollections.filter((collection) => {
      const collectionEntryConfigId =
        typeof collection.entryConfigRef === "string" ? collection.entryConfigRef : collection.entryConfigRef.id;
      return collectionEntryConfigId === selectedEntryConfigId;
    });

    // Get all individual values from all matching collections
    const allValues: Array<{ index: number; value: number; timestamp: unknown }> = [];

    matchingCollections.forEach((collection) => {
      const values = getAllEntryValues(
        collection,
        imageMeasurementMetric,
        standCountMetric,
        rowPlantedStandCountMetric,
        selectedEntryConfig
      );
      values.forEach((value) => {
        allValues.push({
          index: allValues.length + 1,
          value,
          timestamp: collection.createdAt,
        });
      });
    });

    return allValues;
  };

  // Get first image and total count from entry collections for the selected trait
  const getTraitImages = (plotData: HeatmapDataPoint) => {
    if (!selectedEntryConfigId) return { firstImage: null, totalCount: 0 };

    // Filter to only entry collections that match the selected trait
    const matchingCollections = plotData.rawData.entryCollections.filter((collection) => {
      const collectionEntryConfigId =
        typeof collection.entryConfigRef === "string" ? collection.entryConfigRef : collection.entryConfigRef.id;
      return collectionEntryConfigId === selectedEntryConfigId;
    });

    const allImages = matchingCollections.flatMap((collection) => extractImagesFromEntryCollection(collection));
    return {
      firstImage: allImages[0] || null,
      totalCount: allImages.length,
    };
  };

  // Truncate text for display in cells (max ~10 characters to fit in cell)
  const truncateForCell = (text: string, maxLength: number = 10): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 1) + "…";
  };

  // Format value for display
  const formatValueForDisplay = (value: unknown, entryConfig?: EntryConfigUnion, truncate: boolean = true): string => {
    if (value === null || value === undefined) return "";

    // Handle arrays (e.g., multi-select categories)
    if (Array.isArray(value)) {
      const displayStr = value.join(", ");
      return truncate ? truncateForCell(displayStr) : displayStr;
    }

    // Handle boolean values
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Handle Firebase Timestamp objects (date type)
    if (isTimestampLike(value) && "nanoseconds" in value) {
      const date = new Date(value.seconds * 1000);
      return date.toLocaleDateString();
    }

    // Handle numbers
    if (typeof value === "number") {
      // Use the shared formatMetricValue function for measurement types
      if (
        measurementMetric &&
        (entryConfig?.type === "imageMeasurement" ||
          entryConfig?.type === "standCountMeasurement" ||
          entryConfig?.type === "rowPlantedStandCount")
      ) {
        return formatMetricValue(value, measurementMetric, entryConfig);
      }

      // Leaf area index is stored as a 0–1 canopy fraction; percentage entries are stored
      // already scaled 0–100 (the iOS slider saves the shown value), so only LAI is scaled here.
      if (entryConfig?.type === "leafAreaIndex") {
        return `${(value * 100).toFixed(1)}%`;
      }
      if (entryConfig?.type === "percentage") {
        return `${value.toFixed(1)}%`;
      }

      // Timer values are raw elapsed milliseconds — render as mm:ss.SS.
      if (entryConfig?.type === "timer") {
        return formatTimerDuration(value) || value.toFixed(1);
      }

      return value.toFixed(1);
    }

    // Handle everything else (strings, etc.)
    const stringValue = String(value);
    // Truncate text entries if requested (for cell display)
    if (truncate && entryConfig?.type === "text") {
      return truncateForCell(stringValue);
    }
    return stringValue;
  };

  // Get value extent for legend
  const valueExtent = useMemo(() => {
    if (!selectedEntryConfig || heatmapData.length === 0) return null;

    const isTextBased =
      selectedEntryConfig?.type === "text" ||
      selectedEntryConfig?.type === "category" ||
      selectedEntryConfig?.type === "boolean" ||
      selectedEntryConfig?.type === "transcription" ||
      selectedEntryConfig?.type === "photo";

    if (isTextBased) return null;

    // Grade is categorical — uses a swatch legend rendered separately.
    if (isGradeMetric) return null;

    // For dates, convert to timestamps; for numbers, use as-is
    // Filter out error cells to avoid skewing the legend extent
    const numericValues = heatmapData
      .filter((d) => !d.hasError && d.value !== null && d.value !== undefined)
      .map((d) => {
        const val = d.value;
        if (val && typeof val === "object" && "seconds" in val) {
          return val.seconds * 1000;
        }
        return val as number;
      });

    const extent = d3.extent(numericValues) as [number, number];
    return extent.every((v) => v !== undefined) ? extent : null;
  }, [heatmapData, isGradeMetric, selectedEntryConfig]);

  // Format a legend endpoint to match the cell/tooltip formatting for the
  // selected trait type (e.g. timer endpoints show mm:ss.SS, not raw ms).
  const formatLegendEndpoint = (value: number): string => {
    if (selectedEntryConfig?.type === "date") {
      return new Date(value).toLocaleDateString();
    }
    if (selectedEntryConfig?.type === "leafAreaIndex") {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (selectedEntryConfig?.type === "percentage") {
      return `${value.toFixed(1)}%`;
    }
    if (selectedEntryConfig?.type === "timer") {
      return formatTimerDuration(value) || value.toFixed(1);
    }
    if (
      measurementMetric &&
      (selectedEntryConfig?.type === "imageMeasurement" ||
        selectedEntryConfig?.type === "standCountMeasurement" ||
        selectedEntryConfig?.type === "rowPlantedStandCount")
    ) {
      return formatMetricValue(value, measurementMetric, selectedEntryConfig);
    }
    return value.toFixed(1);
  };

  // Render number line for tooltip
  const renderNumberLine = (data: HeatmapDataPoint) => {
    const samples = getSampleValues(data);
    if (samples.length === 0) return null;

    const width = 200;
    const height = 60;
    const padding = { top: 20, right: 10, bottom: 20, left: 10 };

    const values = samples.map((s) => s.value).filter((v): v is number => typeof v === "number");
    if (values.length === 0) return null;
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // Check if we should display as percentage
    const isPercentageType =
      selectedEntryConfig?.type === "leafAreaIndex" || selectedEntryConfig?.type === "percentage";

    const isMeasurementType =
      !!measurementMetric &&
      (selectedEntryConfig?.type === "imageMeasurement" ||
        selectedEntryConfig?.type === "standCountMeasurement" ||
        selectedEntryConfig?.type === "rowPlantedStandCount");

    const isTimerType = selectedEntryConfig?.type === "timer";

    const formatNumber = (n: number): string => {
      if (isPercentageType) return `${(n * 100).toFixed(1)}%`;
      if (isMeasurementType) return formatMetricValue(n, measurementMetric!, selectedEntryConfig);
      if (isTimerType) return formatTimerDuration(n) || n.toFixed(1);
      return n.toFixed(1);
    };

    // Scale for positioning values on the number line
    const xScale = d3
      .scaleLinear()
      .domain([minValue, maxValue])
      .range([padding.left, width - padding.right]);

    return (
      <svg
        width={width}
        height={height}
        className="mt-2">
        {/* Number line */}
        <line
          x1={padding.left}
          y1={height / 2}
          x2={width - padding.right}
          y2={height / 2}
          stroke="currentColor"
          strokeWidth={2}
          className="text-gray-400"
        />

        {/* Min/Max labels */}
        <text
          x={padding.left}
          y={height - 5}
          textAnchor="start"
          className="text-xs fill-gray-600">
          {formatNumber(minValue)}
        </text>
        <text
          x={width - padding.right}
          y={height - 5}
          textAnchor="end"
          className="text-xs fill-gray-600">
          {formatNumber(maxValue)}
        </text>

        {/* Sample points */}
        {samples
          .filter((sample) => typeof sample.value === "number")
          .map((sample, i) => {
            const numericValue = sample.value as number;
            return (
              <g key={i}>
                <circle
                  cx={xScale(numericValue)}
                  cy={height / 2}
                  r={4}
                  fill="currentColor"
                  className="text-blue-600"
                />
                <text
                  x={xScale(numericValue)}
                  y={15}
                  textAnchor="middle"
                  className="text-xs fill-gray-800 font-semibold">
                  {formatNumber(numericValue)}
                </text>
              </g>
            );
          })}
      </svg>
    );
  };

  // Show loading state
  if (plotsLoading || dataLoading) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">Plot Map View</CardTitle>
              {aiTraitsButton}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading plot data...</div>
            </div>
          </CardContent>
        </Card>
        {downloadDialog}
      </>
    );
  }

  // Show error state
  if (plotsError || dataError) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">Plot Map View</CardTitle>
              {aiTraitsButton}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-red-500">Error loading data: {plotsError?.message || dataError?.message}</div>
            </div>
          </CardContent>
        </Card>
        {downloadDialog}
      </>
    );
  }

  // Show empty state if no plots
  if (plots.length === 0) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">Plot Map View</CardTitle>
              {aiTraitsButton}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">No plots available for this task</div>
            </div>
          </CardContent>
        </Card>
        {downloadDialog}
      </>
    );
  }

  // Show empty state if no EntryConfigs available
  if (availableEntryConfigs.length === 0) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">{displayMode === "grid" ? "Plot Map View" : "Plot List View"}</CardTitle>
              {aiTraitsButton}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">No Traits available for this task</div>
            </div>
          </CardContent>
        </Card>
        {downloadDialog}
      </>
    );
  }

  // Show empty state if in grid mode but no plots have valid positions
  // This can happen if all plots lack row/column data but we default to grid mode
  if (displayMode === "grid" && maxRows === 0 && maxCols === 0 && plots.length > 0) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">Plot Map View</CardTitle>
              {aiTraitsButton}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="text-gray-500 mb-2">No plots have row/column position data</div>
              <div className="text-sm text-gray-400">
                To display plots in a grid view, upload a plot map with row and column information.
              </div>
            </div>
          </CardContent>
        </Card>
        {downloadDialog}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-lg">{displayMode === "grid" ? "Plot Map View" : "Plot List View"}</CardTitle>
          <div className="flex items-start gap-2">
            {aiTraitsButton}
            {showDeferredAnalysisButton && (
              <div className="flex flex-col items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "relative flex items-center gap-1 pr-7 border-2 font-semibold shadow-md transition-all",
                    "border-emerald-700 bg-emerald-600 text-white shadow-emerald-200/80 hover:bg-emerald-700 hover:text-white",
                    "focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2",
                    "disabled:border-emerald-300 disabled:bg-emerald-100 disabled:text-emerald-900 disabled:shadow-none"
                  )}
                  data-testid="run-deferred-analysis"
                  title={deferredAnalysisSummary.disabledReason}
                  disabled={isDeferredAnalysisButtonBusy || deferredAnalysisSummary.eligibleCount === 0}
                  onClick={handleRunDeferredAnalysis}>
                  {isDeferredAnalysisButtonBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  ) : (
                    <PlayCircle className="h-4 w-4 text-white" />
                  )}
                  <span>{deferredAnalysisButtonLabel}</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          aria-label="Pending analysis information"
                          className="absolute right-1 top-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-white/90 hover:text-white"
                          onClick={(event) => event.stopPropagation()}>
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="end"
                        className="max-w-xs text-sm leading-5">
                        Some data collectors did not run their image analysis at the time they captured the image. Click
                        here to run it now and populate "Pending" observations with results.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>
                <span
                  aria-hidden={!deferredAnalysisProgressText}
                  className={cn(
                    "min-h-4 text-center text-xs font-medium text-emerald-800",
                    !deferredAnalysisProgressText && "invisible"
                  )}>
                  {deferredAnalysisProgressText || "Analysis progress"}
                </span>
              </div>
            )}
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                data-testid="open-download-dialog"
                onClick={() => {
                  setDialogMode("auto");
                  setIsDownloadDialogOpen(true);
                }}>
                {isExportRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isExportRunning
                  ? "Export Running"
                  : hasRecentCompletedExport
                    ? "Download Ready"
                    : "Download"}
              </Button>
              <span
                aria-hidden={!exportJobProgressText}
                className={cn(
                  "min-h-4 text-center text-xs font-medium",
                  exportJob?.status === "failed"
                    ? "text-destructive"
                    : "text-slate-700",
                  !exportJobProgressText && "invisible"
                )}>
                {exportJobProgressText || "Export progress"}
              </span>
            </div>
            <Select
              value={selectedEntryConfigId || ""}
              onValueChange={onEntryConfigChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Trait" />
              </SelectTrigger>
              <SelectContent>
                {sortTraitsByTypeAndName(availableEntryConfigs).map((entryConfig) => {
                  const Icon = getTraitIcon(entryConfig.type);
                  const traitColor = getTraitColor(entryConfig.type);
                  const isFieldAdded = fieldAddedTraitIds.has(entryConfig.id!);
                  return (
                    <SelectItem
                      key={entryConfig.id}
                      value={entryConfig.id!}>
                      <div className="flex items-center gap-2">
                        <Icon
                          className={cn("h-4 w-4", traitColor)}
                          aria-hidden="true"
                        />
                        <span>
                          {entryConfig.name}
                          {isFieldAdded && " *"}
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Heatmap View */}
        {selectedEntryConfig && (
          <div className="mb-4">
            <h3 className="text-base font-medium">{selectedEntryConfig.name}</h3>
            <p className="text-sm text-gray-500">{selectedEntryConfig.description}</p>
          </div>
        )}

        {selectedEntryConfig && measurementMetric && onMeasurementMetricChange && (
          <MeasurementMetricTabs
            entryConfig={selectedEntryConfig}
            selectedMetric={measurementMetric}
            onMetricChange={onMeasurementMetricChange}
          />
        )}

        <div className="relative">
          {/* Scroll indicator - right fade gradient */}
          {canScrollRight && (
            <div
              className="absolute right-0 top-0 bottom-4 z-10 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none"
              aria-hidden="true"
            />
          )}
          {/* Scroll indicator - bottom fade gradient (the grid viewport is height-capped) */}
          {canScrollDown && (
            <div
              className="absolute left-0 right-0 bottom-4 z-10 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"
              aria-hidden="true"
            />
          )}
          <div
            ref={gridContainerRef}
            data-testid="heatmap-scroll-container"
            className={cn(
              "w-full pb-4",
              // Grid mode scrolls inside its own bounded viewport rather than letting
              // the page scroll, so the row/column headers can stay pinned to its
              // edges (Excel freeze-panes). 70vh leaves the card header and page
              // chrome on screen while the grid is scrolled. List mode has no headers
              // to freeze, so it keeps scrolling with the page.
              displayMode === "grid" ? "overflow-auto max-h-[70vh]" : "overflow-x-auto"
            )}
            onScroll={() => {
              updateScrollIndicator();
              setTooltipData(null); // Hide tooltip on scroll to prevent detachment from cell
            }}>
            {/* Column headers - only show in grid mode (semantic row/col layout) */}
            {displayMode === "grid" && (
              // Use width, not minWidth, so the divider ends at the final data column;
              // the borderless body can use minWidth and stretch with a wider viewport.
              <div
                data-testid="heatmap-column-headers"
                className="sticky top-0 z-20 flex bg-white border-b border-gray-200"
                style={{ width: gridWidth }}>
                <div
                  data-testid="heatmap-corner-header"
                  className="sticky left-0 z-[1] flex-shrink-0 bg-white border-r border-gray-200"
                  style={HEADER_CELL_STYLE}>
                  <span className="text-lg font-medium text-gray-500">#</span>
                </div>
                {Array.from({ length: maxCols - minCols + 1 }, (_, i) => minCols + i).map((col) => (
                  <div
                    key={`col-${col}`}
                    className="flex-shrink-0"
                    style={HEADER_CELL_STYLE}>
                    <span className="text-lg font-medium text-gray-600">{col}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Grid rows with relative positioning for tooltip */}
            <div
              data-testid="heatmap-grid-body"
              className="relative"
              style={{
                // The body may grow with its viewport, but it must stay aligned with
                // the row header and all data columns when horizontal scrolling begins.
                minWidth: displayMode === "grid" ? gridWidth : actualWidth,
              }}>
              {Array.from({ length: maxRows - minRows + 1 }, (_, i) => minRows + i).map((row, rowIndex) => (
                <div
                  key={`row-${row}`}
                  className="flex">
                  {/* Row header - only show in grid mode */}
                  {displayMode === "grid" && (
                    <div
                      data-testid="heatmap-row-header"
                      className="sticky left-0 z-[15] flex-shrink-0 bg-white border-r border-gray-200"
                      style={HEADER_CELL_STYLE}>
                      <span className="text-lg font-medium text-gray-600">{row}</span>
                    </div>
                  )}

                  {/* SVG for this row's cells */}
                  <svg
                    width={actualWidth}
                    height={GRID_CELL_SIZE}
                    className="heat-map-row flex-shrink-0">
                    {/* Heat map cells for this row */}
                    {heatmapData
                      .filter((d) => d.row === row)
                      .map((d) => {
                        const isPhotoType = selectedEntryConfig?.type === "photo";
                        const { firstImage, totalCount } = isPhotoType
                          ? getTraitImages(d)
                          : { firstImage: null, totalCount: 0 };
                        const deferredStatusDisplay = d.analysisStatus
                          ? DEFERRED_STATUS_DISPLAY[d.analysisStatus]
                          : null;
                        const deferredStatusLabel = deferredStatusDisplay?.label ?? null;
                        const deferredStatusFill = deferredStatusDisplay?.fill ?? null;
                        const deferredStatusStroke = deferredStatusDisplay?.stroke ?? null;
                        const isCollectedEmpty = d.isCollectedEmpty ?? false;
                        const hasDisplayValue = d.value !== null && d.value !== undefined;

                        // Helper to handle tooltip positioning (viewport-relative for portal)
                        const handleMouseEnter = (element: Element) => {
                          const cellRect = element.getBoundingClientRect();
                          setTooltipData({
                            x: cellRect.left + cellRect.width / 2,
                            y: cellRect.top,
                            bottom: cellRect.bottom,
                            data: d,
                          });
                        };

                        return (
                          <g key={`${d.row}-${d.col}`}>
                            {isPhotoType ? (
                              // Photo thumbnail cell
                              <foreignObject
                                x={xScale(String(d.col)) || 0}
                                y={0}
                                width={xScale.bandwidth()}
                                height={GRID_CELL_SIZE}
                                className="cursor-pointer"
                                onClick={() => handleClick(d)}
                                onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}
                                onMouseLeave={() => setTooltipData(null)}>
                                <div className="w-full h-full relative border border-white">
                                  {firstImage ? (
                                    <>
                                      <LazyStorageImage
                                        storagePath={firstImage.url}
                                        uploadStatus={firstImage.uploadStatus}
                                        updatedAt={firstImage.updatedAt}
                                        alt={d.plotId}
                                        className="w-full h-full"
                                      />
                                      {totalCount > 1 && (
                                        <div className="absolute bottom-1 right-1 bg-gray-900/80 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                                          +{totalCount - 1}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    // Gray placeholder for plots with no photos
                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                      <ImageOff className="h-6 w-6 text-gray-300" />
                                    </div>
                                  )}
                                  {/* PlotID overlay - show in list mode since layout has no semantic meaning */}
                                  {displayMode === "list" && (
                                    <div className="absolute top-1 left-1 bg-black/60 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                                      {d.plotId}
                                    </div>
                                  )}
                                </div>
                              </foreignObject>
                            ) : (
                              // Original colored rect for non-photo types
                              <>
                                <rect
                                  x={xScale(String(d.col)) || 0}
                                  y={0}
                                  width={xScale.bandwidth()}
                                  height={GRID_CELL_SIZE}
                                  fill={
                                    d.hasError
                                      ? "#fecaca"
                                      : (deferredStatusFill ??
                                        (hasDisplayValue ? colorScale(d.value) : isCollectedEmpty ? "#f3f4f6" : "#ccc"))
                                  }
                                  stroke={
                                    d.hasError
                                      ? "#ef4444"
                                      : (deferredStatusStroke ?? (isCollectedEmpty ? "#d1d5db" : "white"))
                                  }
                                  strokeWidth={d.hasError || deferredStatusStroke || isCollectedEmpty ? 2 : 1}
                                  className="cursor-pointer transition-opacity hover:opacity-80"
                                  onClick={() => handleClick(d)}
                                  onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}
                                  onMouseLeave={() => setTooltipData(null)}
                                />

                                {/* PlotID label - show in list mode since layout has no semantic meaning */}
                                {displayMode === "list" && (
                                  <text
                                    x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                    y={20}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className="text-xs font-medium fill-gray-500 pointer-events-none">
                                    {d.plotId}
                                  </text>
                                )}

                                {/* Data label or error indicator - adjust position in list mode to accommodate plotID */}
                                {d.hasError ? (
                                  // Error indicator: exclamation mark in circle
                                  <>
                                    <circle
                                      cx={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                      cy={displayMode === "list" ? 56 : 48}
                                      r={16}
                                      className="fill-red-500 pointer-events-none"
                                    />
                                    <text
                                      x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                      y={displayMode === "list" ? 56 : 48}
                                      textAnchor="middle"
                                      dominantBaseline="central"
                                      className="text-xl font-bold fill-white pointer-events-none">
                                      !
                                    </text>
                                  </>
                                ) : deferredStatusLabel ? (
                                  <text
                                    x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                    y={displayMode === "list" ? 56 : 48}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className="text-sm font-semibold fill-gray-800 pointer-events-none"
                                    style={{ filter: "drop-shadow(0px 1px 0px white)" }}>
                                    {deferredStatusLabel}
                                  </text>
                                ) : (
                                  <>
                                    <text
                                      x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                      y={
                                        selectedEntryConfig?.type === "standCountMeasurement" &&
                                        measurementMetric === "countPerM2" &&
                                        typeof d.value === "number"
                                          ? displayMode === "list"
                                            ? 50
                                            : 42
                                          : displayMode === "list"
                                            ? 56
                                            : 48
                                      }
                                      textAnchor="middle"
                                      dominantBaseline="central"
                                      className="text-base font-bold fill-gray-800 pointer-events-none"
                                      style={{ filter: "drop-shadow(0px 1px 0px white)" }}>
                                      {isCollectedEmpty ? "--" : formatValueForDisplay(d.value, selectedEntryConfig)}
                                      {d.analysisUnreviewed ? "*" : ""}
                                    </text>
                                    {selectedEntryConfig?.type === "standCountMeasurement" &&
                                      measurementMetric === "countPerM2" &&
                                      typeof d.value === "number" && (
                                        <text
                                          x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                          y={displayMode === "list" ? 68 : 60}
                                          textAnchor="middle"
                                          dominantBaseline="central"
                                          className="text-xs fill-gray-600 pointer-events-none"
                                          style={{ filter: "drop-shadow(0px 1px 0px white)" }}>
                                          ({formatRoundedCountPerSquareMeterPerAcre(d.value)}/acre)
                                        </text>
                                      )}
                                    {isGradeMetric && d.gradeDistribution && (
                                      <text
                                        x={(xScale(String(d.col)) || 0) + xScale.bandwidth() / 2}
                                        y={displayMode === "list" ? 68 : 60}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        className="text-xs fill-gray-600 pointer-events-none"
                                        style={{ filter: "drop-shadow(0px 1px 0px white)" }}>
                                        ({Math.round(d.gradeDistribution.dominantFraction * 100)}%)
                                      </text>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </g>
                        );
                      })}
                  </svg>
                </div>
              ))}

              {/* Tooltip overlay - rendered via portal to escape stacking context */}
              {tooltipData &&
                createPortal(
                  <div
                    ref={measureTooltip}
                    data-testid="heatmap-tooltip"
                    data-placement={tooltipPlacement}
                    className="fixed pointer-events-none z-[9999]"
                    style={
                      tooltipPlacement === "above"
                        ? {
                            left: tooltipData.x,
                            top: tooltipData.y - TOOLTIP_CELL_GAP,
                            transform: "translate(-50%, -100%)",
                          }
                        : {
                            left: tooltipData.x,
                            top: tooltipData.bottom + TOOLTIP_CELL_GAP,
                            transform: "translate(-50%, 0)",
                          }
                    }>
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-none">
                      <div className="space-y-2">
                        <div className="font-semibold text-sm">{tooltipData.data.plotId}</div>
                        {/* Row/Column info - only meaningful in grid mode */}
                        {displayMode === "grid" && (
                          <div className="text-xs text-gray-500">
                            Row {tooltipData.data.row}, Column {tooltipData.data.col}
                          </div>
                        )}
                        <div className="border-t pt-2">
                          <div className="text-xs text-gray-500 mb-1">{selectedEntryConfig?.name}</div>
                          {/* Error state tooltip */}
                          {tooltipData.data.analysisStatus ? (
                            <div className="max-w-xs">
                              <div className="text-sm font-medium capitalize">
                                {DEFERRED_STATUS_DISPLAY[tooltipData.data.analysisStatus].tooltipLabel} analysis
                              </div>
                              <p className="text-sm text-gray-600">
                                {tooltipData.data.analysisStatus === "failed" && tooltipData.data.analysisError
                                  ? tooltipData.data.analysisError
                                  : DEFERRED_STATUS_DISPLAY[tooltipData.data.analysisStatus].tooltipDescription}
                              </p>
                            </div>
                          ) : tooltipData.data.hasError ? (
                            <div className="max-w-xs">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-bold text-sm">!</span>
                                </div>
                                <span className="text-red-700 font-medium text-sm">Unable to calculate</span>
                              </div>
                              <p className="text-sm text-gray-600">
                                {tooltipData.data.errorReason === "no_valid_counts"
                                  ? "Multiple samples exist but none have valid object counts for calculating a weighted average."
                                  : "No valid entries found for this metric."}
                              </p>
                            </div>
                          ) : selectedEntryConfig?.type === "photo" ? (
                            // Photo-specific tooltip content
                            (() => {
                              const { firstImage, totalCount } = getTraitImages(tooltipData.data);
                              const { contributorCount } = getPhotoStats(
                                tooltipData.data.rawData.entryCollections,
                                selectedEntryConfigId!
                              );
                              const collectedEntryCount = tooltipData.data.collectedEntryCount ?? 0;

                              if (!firstImage && collectedEntryCount > 0) {
                                return (
                                  <div className="max-w-xs">
                                    <div className="text-sm font-medium">No image available</div>
                                    <p className="text-sm text-gray-600">
                                      This plot has a photo entry, but no image has been uploaded yet.
                                    </p>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {collectedEntryCount} sample{collectedEntryCount !== 1 ? "s" : ""}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="flex items-center gap-3">
                                  {firstImage && (
                                    <div className="w-16 h-16 rounded overflow-hidden border flex-shrink-0">
                                      <StorageImage
                                        storagePath={firstImage.url}
                                        uploadStatus={firstImage.uploadStatus}
                                        updatedAt={firstImage.updatedAt}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  )}
                                  <div className="text-sm">
                                    {totalCount} photo{totalCount !== 1 ? "s" : ""} from {contributorCount}{" "}
                                    {contributorCount !== 1 ? "people" : "person"}
                                  </div>
                                </div>
                              );
                            })()
                          ) : tooltipData.data.isCollectedEmpty && !isGradeMetric ? (
                            <div className="flex items-center justify-between gap-3">
                              <div className="max-w-xs">
                                <div className="text-sm font-medium">Collected, no value</div>
                                <p className="text-sm text-gray-600">
                                  This plot has collected data, but no value for the selected metric.
                                </p>
                                <div className="text-xs text-gray-500 mt-1">
                                  {tooltipData.data.collectedEntryCount} sample
                                  {tooltipData.data.collectedEntryCount !== 1 ? "s" : ""}
                                </div>
                              </div>
                              {(() => {
                                const { firstImage, totalCount } = getTraitImages(tooltipData.data);
                                if (!firstImage) return null;

                                return (
                                  <div className="w-12 h-12 rounded overflow-hidden border flex-shrink-0 relative">
                                    <StorageImage
                                      storagePath={firstImage.url}
                                      uploadStatus={firstImage.uploadStatus}
                                      updatedAt={firstImage.updatedAt}
                                      alt="Preview"
                                      className="w-full h-full object-cover"
                                    />
                                    {totalCount > 1 && (
                                      <div className="absolute top-0.5 right-0.5 bg-gray-900/80 text-white text-[10px] font-medium px-1 rounded">
                                        +{totalCount - 1}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : selectedEntryConfig?.type === "text" ? (
                            // Text-specific tooltip content - show full text value
                            <div className="max-w-xs">
                              <div className="text-sm break-words whitespace-pre-wrap">
                                {formatValueForDisplay(tooltipData.data.value, selectedEntryConfig, false)}
                              </div>
                            </div>
                          ) : selectedEntryConfig?.type === "boolean" ? (
                            // Boolean-specific tooltip content
                            <div className="text-sm font-medium">
                              {formatValueForDisplay(tooltipData.data.value, selectedEntryConfig, false)}
                            </div>
                          ) : isGradeMetric ? (
                            // Grade-specific tooltip — stacked horizontal bar +
                            // per-bucket count list. Loading metadata? Tell the user.
                            <div className="text-sm">
                              {gradeMetadataLoading && !tooltipData.data.gradeDistribution ? (
                                <div className="text-xs text-muted-foreground">Loading distribution…</div>
                              ) : tooltipData.data.gradeDistribution && grading ? (
                                <div className="space-y-2 min-w-[200px]">
                                  <div className="text-xs">
                                    <span className="font-medium">Dominant:</span>{" "}
                                    {tooltipData.data.gradeDistribution.dominantLabel ?? "—"}
                                    {tooltipData.data.gradeDistribution.dominantLabel != null && (
                                      <>
                                        {" "}— {Math.round(
                                          tooltipData.data.gradeDistribution.dominantFraction * 100
                                        )}% ({tooltipData.data.gradeDistribution.counts[
                                          tooltipData.data.gradeDistribution.dominantLabel
                                        ] ?? 0}{" "}
                                        of {tooltipData.data.gradeDistribution.totalObjects} objects)
                                      </>
                                    )}
                                  </div>
                                  <div className="flex h-2 w-full overflow-hidden rounded">
                                    {grading.buckets.map((bucket) => {
                                      const count =
                                        tooltipData.data.gradeDistribution!.counts[bucket.label] ?? 0;
                                      const pct =
                                        tooltipData.data.gradeDistribution!.totalObjects > 0
                                          ? (count / tooltipData.data.gradeDistribution!.totalObjects) * 100
                                          : 0;
                                      if (pct === 0) return null;
                                      return (
                                        <div
                                          key={bucket.id}
                                          style={{
                                            flexBasis: `${pct}%`,
                                            backgroundColor: colorScale(bucket.label) as string,
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <ul className="space-y-1 text-xs">
                                    {grading.buckets.map((bucket) => {
                                      const count =
                                        tooltipData.data.gradeDistribution!.counts[bucket.label] ?? 0;
                                      const pct =
                                        tooltipData.data.gradeDistribution!.totalObjects > 0
                                          ? (count / tooltipData.data.gradeDistribution!.totalObjects) * 100
                                          : 0;
                                      return (
                                        <li key={bucket.id} className="flex items-center gap-2">
                                          <span
                                            className="inline-block h-2 w-2 rounded-sm"
                                            style={{ backgroundColor: colorScale(bucket.label) as string }}
                                          />
                                          <span className="flex-1">{bucket.label}</span>
                                          <span className="tabular-nums">
                                            {count} ({pct.toFixed(0)}%)
                                          </span>
                                        </li>
                                      );
                                    })}
                                    {tooltipData.data.gradeDistribution.ungraded > 0 && (
                                      <li className="flex items-center gap-2 text-muted-foreground">
                                        <span className="inline-block h-2 w-2 rounded-sm bg-gray-300" />
                                        <span className="flex-1">Ungraded</span>
                                        <span className="tabular-nums">
                                          {tooltipData.data.gradeDistribution.ungraded}
                                        </span>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground">No grade data</div>
                              )}
                            </div>
                          ) : (
                            // Original tooltip content for non-photo/non-text types
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <div className="text-xs">
                                  {(() => {
                                    const stats = getTraitStats(
                                      tooltipData.data.rawData.entryCollections,
                                      selectedEntryConfigId!
                                    );
                                    return `${stats.sampleCount} sample${stats.sampleCount !== 1 ? "s" : ""}`;
                                  })()}
                                </div>
                                {renderNumberLine(tooltipData.data)}
                              </div>
                              {(() => {
                                const { firstImage, totalCount } = getTraitImages(tooltipData.data);
                                if (!firstImage) return null;

                                return (
                                  <div className="w-12 h-12 rounded overflow-hidden border flex-shrink-0 relative">
                                    <StorageImage
                                      storagePath={firstImage.url}
                                      uploadStatus={firstImage.uploadStatus}
                                      updatedAt={firstImage.updatedAt}
                                      alt="Preview"
                                      className="w-full h-full object-cover"
                                    />
                                    {totalCount > 1 && (
                                      <div className="absolute top-0.5 right-0.5 bg-gray-900/80 text-white text-[10px] font-medium px-1 rounded">
                                        +{totalCount - 1}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        <TooltipPlotMetadata
                          plot={tooltipData.data.rawData.plot}
                          plotMap={experiment?.plotMap}
                        />
                      </div>
                      {/* Arrow, on whichever edge faces the cell */}
                      <div
                        className={cn(
                          "absolute left-1/2 transform -translate-x-1/2",
                          tooltipPlacement === "above" ? "bottom-0 translate-y-full" : "top-0 -translate-y-full"
                        )}
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: "6px solid transparent",
                          borderRight: "6px solid transparent",
                          ...(tooltipPlacement === "above"
                            ? { borderTop: "6px solid white" }
                            : { borderBottom: "6px solid white" }),
                        }}
                      />
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
        </div>

        {hasUnreviewedDeferredResults && (
          <div className="mt-3 flex justify-center text-xs font-medium text-amber-700">* Unreviewed</div>
        )}

        {/* Intensity Legend */}
        {valueExtent && (
          <div className="mt-6 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-medium text-gray-700">
                {selectedEntryConfig?.type === "date"
                  ? "Date Range"
                  : selectedEntryConfig?.type === "leafAreaIndex" || selectedEntryConfig?.type === "percentage"
                    ? "Percentage Scale"
                    : selectedEntryConfig?.type === "timer"
                      ? "Duration Scale"
                      : "Intensity Scale"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">
                  {formatLegendEndpoint(valueExtent[0])}
                </span>
                <svg
                  width="200"
                  height="20">
                  <defs>
                    <linearGradient
                      id="legend-gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%">
                      {Array.from({ length: 10 }, (_, i) => {
                        const t = i / 9;
                        const value = valueExtent[0] + t * (valueExtent[1] - valueExtent[0]);
                        // Use the same custom interpolator as the heatmap (75% of the range)
                        const color = d3.interpolateYlOrRd(t * 0.75);
                        return (
                          <stop
                            key={i}
                            offset={`${t * 100}%`}
                            stopColor={color}
                          />
                        );
                      })}
                    </linearGradient>
                  </defs>
                  <rect
                    x="0"
                    y="0"
                    width="200"
                    height="20"
                    fill="url(#legend-gradient)"
                    stroke="#d1d5db"
                    strokeWidth="1"
                    rx="2"
                  />
                </svg>
                <span className="text-xs text-gray-600">
                  {formatLegendEndpoint(valueExtent[1])}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Category Legend */}
        {selectedEntryConfig?.type === "category" && selectedEntryConfig.categoryConfig?.choices && (
          <div className="mt-6 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Categories</span>
              <div className="flex flex-wrap gap-2 justify-center">
                {selectedEntryConfig.categoryConfig.choices.map((choice, index) => {
                  const lightColors = [
                    "#e3f2fd",
                    "#f3e5f5",
                    "#e8f5e9",
                    "#fff3e0",
                    "#fce4ec",
                    "#e0f2f1",
                    "#f1f8e9",
                    "#ede7f6",
                  ];
                  const color = lightColors[index % lightColors.length];
                  return (
                    <div
                      key={choice}
                      className="flex items-center gap-1">
                      <div
                        className="w-4 h-4 rounded border border-gray-300"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-xs text-gray-700">{choice}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Boolean Legend */}
        {selectedEntryConfig?.type === "boolean" && (
          <div className="mt-6 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Values</span>
              <div className="flex gap-4 justify-center">
                <div className="flex items-center gap-1">
                  <div
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: "#e8f5e9" }}
                  />
                  <span className="text-xs text-gray-700">Yes</span>
                </div>
                <div className="flex items-center gap-1">
                  <div
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: "#ffebee" }}
                  />
                  <span className="text-xs text-gray-700">No</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Field-added trait indicator */}
        {fieldAddedTraitIds.size > 0 && (
          <div className="mt-4 flex justify-end">
            <span className="text-xs text-muted-foreground">* Added in field</span>
          </div>
        )}
      </CardContent>

      {downloadDialog}
    </Card>
  );
};

export default D3HeatmapGrid;
